import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../i18n';
import {
  cancelAdminPaymentRequest,
  createAdminDepositRequest,
  extendAdminDepositSession,
  createAdminWithdrawRequest,
  fetchAdminPaymentHistory,
  fetchAdminPaymentSummary,
  fetchAdminPaymentLimits,
  fetchAdminDepositWalletWaitStatus,
  recheckAdminPaymentRequest,
  requestAdminDepositAddress,
} from '../../api/admin';
import styles from './PaymentPanel.module.css';

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch (__) {
      return false;
    }
  }
}

function money(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function shortAddress(value = '') {
  const v = String(value || '').trim();
  if (!v) return '-';
  if (v.length <= 18) return v;
  return `${v.slice(0, 9)}...${v.slice(-7)}`;
}

function normalizeError(error) {
  const data = error?.response?.data;
  return data?.message || data?.cashier?.message || data?.cashier?.code || error?.message || t('요청 처리 중 오류가 발생했습니다.');
}

function statusLabel(status = '') {
  const s = String(status || '').toUpperCase();
  if (s === 'FINAL_DONE' || s === 'DONE') return t('완료');
  if (s === 'IN_PROGRESS') return t('진행중');
  if (s === 'HOLD' || s === 'CRITICAL_REVIEW') return t('검토필요');
  if (s === 'CANCELED') return t('취소');
  if (s === 'FAILED') return t('실패');
  return s || '-';
}

function requestTypeLabel(type = '') {
  return String(type || '').toLowerCase() === 'withdraw' ? t('환전') : t('충전');
}

function isActiveDeposit(item) {
  if (String(item?.request_type || '').toLowerCase() !== 'deposit') return false;
  const status = String(item?.status || '').toUpperCase();
  return status === 'IN_PROGRESS' || (item?.wallet_queue_waiting === true && status === 'WAITING_WALLET');
}

function hasReceivedDeposit(item) {
  return Number(item?.received_amount || 0) > 0
    || Boolean(item?.tx_hash || item?.tx_id || item?.deposit_id);
}

export default function PaymentPanel() {
  const [mode, setMode] = useState('deposit');
  const [summary, setSummary] = useState(null);
  const [limits, setLimits] = useState(null);
  const [history, setHistory] = useState([]);
  const [justSubmittedDeposit, setJustSubmittedDeposit] = useState(null);
  const [amount, setAmount] = useState('');
  const [senderWallet, setSenderWallet] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [depositInfo, setDepositInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const actionInFlightRef = useRef(false);
  const [message, setMessage] = useState('');
  const [waitingRequestId, setWaitingRequestId] = useState(() => {
    try { return window.sessionStorage.getItem('npc_admin_payment_wait_id') || ''; }
    catch (_) { return ''; }
  });
  const [waitingPosition, setWaitingPosition] = useState(0);

  // The cashier owns the authoritative history. Show its pending records first,
  // and keep the newly accepted request visible until a history refresh sees it.
  const activeDeposits = useMemo(() => {
    const items = history.filter(isActiveDeposit);
    const historyIds = new Set(history.map((item) => String(item?.request_id || '')));
    if (justSubmittedDeposit?.request_id && !historyIds.has(String(justSubmittedDeposit.request_id))
      && isActiveDeposit(justSubmittedDeposit)) {
      items.unshift(justSubmittedDeposit);
    }
    if (waitingRequestId && !historyIds.has(waitingRequestId)
      && !items.some((item) => String(item.request_id) === waitingRequestId)) {
      items.unshift({
        request_id: waitingRequestId,
        request_type: 'deposit',
        status: 'WAITING_WALLET',
        wallet_queue_waiting: true,
        wallet_queue_position: waitingPosition,
      });
    }
    return items.sort((a, b) => {
      const aTime = Date.parse(a.updated_at || a.created_at || '') || 0;
      const bTime = Date.parse(b.updated_at || b.created_at || '') || 0;
      return bTime - aTime;
    });
  }, [history, justSubmittedDeposit, waitingRequestId, waitingPosition]);

  useEffect(() => {
    try {
      if (waitingRequestId) window.sessionStorage.setItem('npc_admin_payment_wait_id', waitingRequestId);
      else window.sessionStorage.removeItem('npc_admin_payment_wait_id');
    } catch (_) { /* private browsing or disabled storage */ }
  }, [waitingRequestId]);

  const paymentNetwork = useMemo(() => ({
    assetCode: 'USDT',
    networkCode: 'TRC20',
    networkName: 'mainnet',
    label: t('USDT TRC20 메인넷'),
  }), []);
  const { assetCode, networkCode, networkName } = paymentNetwork;

  const loadData = useCallback(async () => {
    const [summaryData, historyData] = await Promise.all([
      fetchAdminPaymentSummary(),
      fetchAdminPaymentHistory({ limit: 100 }),
    ]);
    setSummary(summaryData || null);
    setHistory(Array.isArray(historyData?.items) ? historyData.items : []);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadData()
      .catch((error) => {
        if (alive) setMessage(normalizeError(error));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    const timer = setInterval(() => {
      loadData().catch(() => null);
    }, 10000);

    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [loadData]);

  // Read the same effective policy the cashier server uses. An unavailable
  // policy means submissions are disabled instead of guessing a stale 50 USDT.
  useEffect(() => {
    let active = true;
    const readLimits = async () => {
      try {
        const result = await fetchAdminPaymentLimits();
        if (!active) return;
        if (result?.ok !== true || !['minimum_deposit_amount', 'minimum_withdraw_amount', 'maximum_withdraw_amount']
          .every((key) => /^\d+(?:\.\d{1,2})?$/.test(String(result[key] ?? '')))) {
          throw new Error('결제 금액 정책 형식이 올바르지 않습니다.');
        }
        setLimits(result);
      } catch (error) {
        if (active) {
          setLimits(null);
          setMessage(normalizeError(error));
        }
      }
    };
    readLimits();
    const timer = setInterval(readLimits, 30000);
    return () => { active = false; clearInterval(timer); };
  }, []);

  const depositWallet = depositInfo?.deposit_wallet_address || depositInfo?.wallet_address || '';
  const positiveMoney = /^\d+(?:\.\d{1,2})?$/.test(String(amount).trim()) && Number(amount) > 0;
  const amountIsValidForDeposit = Boolean(limits) && positiveMoney && Number(amount) >= Number(limits.minimum_deposit_amount);
  const amountIsValidForWithdraw = Boolean(limits) && positiveMoney
    && Number(amount) >= Number(limits.minimum_withdraw_amount)
    && (Number(limits.maximum_withdraw_amount) === 0 || Number(amount) <= Number(limits.maximum_withdraw_amount));
  const canSubmitDeposit = Boolean(depositWallet) && amountIsValidForDeposit && !waitingRequestId && !activeDeposits.length && !loading && !busyAction;
  const canSubmitWithdraw = Boolean(withdrawAddress.trim()) && amountIsValidForWithdraw && !loading && !busyAction;

  // Poll the original request ID. Never issue another allocation while queued.
  useEffect(() => {
    if (!waitingRequestId) return undefined;
    let active = true;
    let inflight = false;
    const poll = async () => {
      if (inflight) return;
      inflight = true;
      try {
        const state = await fetchAdminDepositWalletWaitStatus(waitingRequestId);
        if (!active) return;
        if (state?.wallet_queue_waiting) {
          setWaitingPosition(Number(state.wallet_queue_position || 0));
          return;
        }
        const finalStatus = String(state?.status || '').toUpperCase();
        if (!state?.queue_resume_terminal && !['CANCELED', 'FAILED', 'FINAL_DONE', 'HOLD', 'CRITICAL_REVIEW'].includes(finalStatus)
          && state?.ok !== false && (state?.deposit_wallet_address || state?.wallet_address)) {
          setDepositInfo(state);
          setWaitingPosition(0);
          setWaitingRequestId('');
          setMessage(t('대기 중이던 입금 지갑이 배정되었습니다. 주소를 확인하세요.'));
        } else {
          setWaitingRequestId('');
          setWaitingPosition(0);
          setMessage(state?.message || t('대기열 요청이 종료되었습니다. 다시 확인하세요.'));
        }
      } catch (error) {
        if (active && error?.response?.status === 400 && error?.response?.data?.cashier?.code === 'REQUEST_NOT_FOUND') {
          setWaitingRequestId('');
          setWaitingPosition(0);
          setMessage(t('대기열 요청이 종료되었거나 만료되었습니다.'));
          return;
        }
        if (active) {
          // Retain the request ID on transient Redis/network errors; retries
          // must not allocate a new wallet or lose the user's place.
          setMessage(normalizeError(error));
        }
      } finally {
        inflight = false;
      }
    };
    poll();
    const timer = setInterval(poll, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [waitingRequestId]);

  const runAction = async (name, fn) => {
    if (actionInFlightRef.current) return null;
    actionInFlightRef.current = true;
    setBusyAction(name);
    setMessage('');
    try {
      const result = await fn();
      await loadData().catch(() => null);
      return result;
    } catch (error) {
      setMessage(normalizeError(error));
      return null;
    } finally {
      actionInFlightRef.current = false;
      setBusyAction('');
    }
  };

  const onIssueDepositAddress = () => {
    if (waitingRequestId) return;
    return runAction('deposit-address', async () => {
      const result = await requestAdminDepositAddress({ asset_code: assetCode, network_code: networkCode, network_name: networkName });
    if (result?.ok === false) throw new Error(result?.message || t('입금주소 발급 실패'));
    if (result?.wallet_queue_waiting) {
      setDepositInfo(null);
      setWaitingRequestId(String(result.request_id || ''));
      setWaitingPosition(Number(result.wallet_queue_position || 0));
      setMessage(t('입금 지갑이 모두 사용 중입니다. 대기열에서 자동 배정을 기다립니다.'));
      return result;
    }
    setWaitingRequestId('');
    setWaitingPosition(0);
    setDepositInfo(result);
    setMessage(t('입금주소가 발급되었습니다. 해당 주소로 송금 후 충전 요청 버튼을 눌러주세요.'));
    return result;
    });
  };

  const onSubmitDeposit = () => runAction('deposit-request', async () => {
    if (!depositWallet) throw new Error(t('먼저 입금주소를 발급하세요.'));
    if (!amountIsValidForDeposit) throw new Error(limits ? `${t('최소 입금금액')}: ${money(limits.minimum_deposit_amount)} USDT (${t('0보다 큰 금액, 소수점 2자리까지')})` : t('결제 정책을 불러오지 못했습니다.'));
    const result = await createAdminDepositRequest({
      amount,
      deposit_from_address: senderWallet,
      bridge_id: depositInfo?.bridge_id,
      bridge_wallet_id: depositInfo?.bridge_wallet_id || depositInfo?.wallet_id,
      bridge_wallet_address: depositInfo?.bridge_wallet_address || depositWallet,
      deposit_wallet_address: depositWallet,
      address_issued_at: depositInfo?.address_issued_at || depositInfo?.issued_at,
      asset_code: assetCode,
      network_code: networkCode,
      network_name: networkName,
    });
    if (result?.ok === false) throw new Error(result?.message || t('충전 요청 실패'));
    if (!result?.request_id) throw new Error(t('충전 신청 결과의 요청 ID가 없습니다. 결제내역을 확인하세요.'));
    const isWaiting = result.wallet_queue_waiting === true;
    setJustSubmittedDeposit({
      ...result,
      request_type: 'deposit',
      status: isWaiting ? 'WAITING_WALLET' : 'IN_PROGRESS',
      amount: result.amount || amount,
      deposit_wallet_address: result.deposit_wallet_address || depositWallet,
      created_at: result.created_at || new Date().toISOString(),
    });
    if (isWaiting) {
      setWaitingRequestId(String(result.request_id));
      setWaitingPosition(Number(result.wallet_queue_position || 0));
    }
    setMessage(isWaiting
      ? t('충전 신청이 접수되어 입금지갑 배정을 기다립니다. 아래에서 진행 상태를 확인하고 취소할 수 있습니다.')
      : t('충전 신청이 등록되었습니다. 아래 진행 중 기록에서 취소할 수 있으며, 실제 입금액은 온체인 확인 후 반영됩니다.'));
    return result;
  });

  const onSubmitWithdraw = () => runAction('withdraw-request', async () => {
    if (!amountIsValidForWithdraw) throw new Error(limits ? `${t('최소 출금금액')}: ${money(limits.minimum_withdraw_amount)} USDT / ${t('최대 출금금액 제한')}: ${Number(limits.maximum_withdraw_amount) > 0 ? money(limits.maximum_withdraw_amount) : 'OFF'} (${t('0보다 큰 금액, 소수점 2자리까지')})` : t('결제 정책을 불러오지 못했습니다.'));
    const result = await createAdminWithdrawRequest({
      amount,
      address: withdrawAddress,
      asset_code: assetCode,
      network_code: networkCode,
      network_name: networkName,
    });
    if (result?.ok === false) throw new Error(result?.message || t('환전 요청 실패'));
    setMessage(t('환전 요청이 등록되었습니다. DB 자산 및 출금가능 검증금액 확인 후 처리됩니다.'));
    return result;
  });

  const onExtend = (item) => runAction(`extend:${item.request_id}`, async () => {
    const result = await extendAdminDepositSession({
      request_id: item.request_id,
      expected_expires_at: item.deposit_session_expires_at,
    });
    if (result?.ok === false) throw new Error(result?.message || t('거래시간 연장 실패'));
    setMessage(result?.duplicated === true
      ? t('이미 연장된 거래시간을 확인했습니다.')
      : t('충전 거래시간을 30분 연장했습니다.'));
    return result;
  });

  const onCancel = (requestId) => runAction(`cancel:${requestId}`, async () => {
    const result = await cancelAdminPaymentRequest(requestId);
    if (result?.ok === false) throw new Error(result?.message || t('취소 실패'));
    setMessage(t('요청 취소 처리를 보냈습니다.'));
    return result;
  });

  const onCancelDeposit = (item) => {
    const requestId = String(item?.request_id || '').trim();
    if (!requestId || busyAction || !isActiveDeposit(item) || hasReceivedDeposit(item)) return;
    // Browser confirmation is required: a cancel does not reverse a transfer
    // already sent on-chain. The cashier performs final ownership/proof checks.
    if (!window.confirm(t('이 충전 신청을 취소하시겠습니까? 이미 USDT를 송금했다면 취소하지 말고 입금 재확인을 진행하세요.'))) return;
    return runAction(`cancel-deposit:${requestId}`, async () => {
      const result = await cancelAdminPaymentRequest(requestId);
      if (result?.ok === false) throw new Error(result?.message || t('충전 신청 취소 실패'));
      if (String(result?.status || '').toUpperCase() !== 'CANCELED') {
        // HOLD/DONE/etc. are NOT confirmation of cancellation. Keep the
        // original request on screen until the cashier provides a terminal state.
        throw new Error(result?.message || t('취소가 확정되지 않았습니다. 결제내역을 재확인하세요.'));
      }
      setHistory((prev) => prev.map((entry) => String(entry.request_id) === requestId
        ? { ...entry, ...result, request_type: 'deposit', status: 'CANCELED' }
        : entry));
      setJustSubmittedDeposit((prev) => String(prev?.request_id || '') === requestId ? null : prev);
      setWaitingRequestId((prev) => prev === requestId ? '' : prev);
      setWaitingPosition((prev) => waitingRequestId === requestId ? 0 : prev);
      // A released wallet must not be offered as a fresh deposit destination.
      setDepositInfo(null);
      setMessage(t('충전 신청 취소가 확인되었습니다. 다시 신청하려면 새 입금주소를 발급받으세요.'));
      return result;
    });
  };

  const onRecheck = (requestId) => runAction(`recheck:${requestId}`, async () => {
    const result = await recheckAdminPaymentRequest(requestId);
    if (result?.ok === false) throw new Error(result?.message || t('재확인 실패'));
    setMessage(t('입금 재확인 요청을 보냈습니다.'));
    return result;
  });

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setMessage('');
    setAmount('');
    if (nextMode === 'withdraw') {
      setSenderWallet('');
    } else {
      setWithdrawAddress('');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.wrap}>
        <div className={styles.breadcrumb}>
          <span className={styles.folderIcon}>📁</span>
          <span className={styles.breadcrumbText}>{t('어드민 결제')}</span>
        </div>

        <div className={styles.assetRow}>
          <div className={styles.assetTitle}>{t('내 자산')} {money(summary?.asset)}</div>
          <div className={styles.assetCoin}>T</div>
        </div>

        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>{t('출금가능 검증금액')}</div>
            <div className={styles.summaryValue}>{money(summary?.verified_withdrawable)}</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>{t('운영수익 정책')}</div>
            <div className={styles.summaryValueSmall}>{t('개인 출금가능금액에 미가산')}</div>
          </div>
        </div>

        <div className={styles.policyBox}>
          {t('어드민 직접 환전은 DB 자산 및 출금가능 검증금액을 기준으로 처리됩니다. 운영수익은 개인 출금가능금액에 포함되지 않습니다.')}
        </div>

        <div className={styles.networkSelectBox}>
          <div className={styles.networkSelectLabel}>{t('결제 네트워크')}</div>
          <select className={styles.networkSelect} value={paymentNetwork.label} disabled>
            <option value={paymentNetwork.label}>{paymentNetwork.label}</option>
          </select>
          <div className={styles.networkSelectHint}>{t('현재 운영 환경은 USDT TRC20 메인넷만 사용합니다.')}</div>
        </div>

        <div className={styles.actionRow}>
          <button type="button" className={mode === 'withdraw' ? styles.btnTealWide : styles.btnOutlineWide} onClick={() => switchMode('withdraw')}>{t('환전하기')}</button>
          <button type="button" className={mode === 'deposit' ? styles.btnTealWide : styles.btnOutlineWide} onClick={() => switchMode('deposit')}>{t('충전하기')}</button>
        </div>

        {message ? <div className={styles.messageBox}>{message}</div> : null}

        {mode === 'deposit' ? (
          <div className={styles.stepBlock}>
            <div className={styles.stepTitle}>{t('Admin Charge')}</div>
            <div className={styles.stepLabel}>{t('내 송금 지갑 주소')}</div>
            <input
              className={styles.stepInputWide}
              value={senderWallet}
              onChange={(e) => setSenderWallet(e.target.value)}
              placeholder={t('선택 입력: 내가 송금하는 지갑 주소')}
            />

            <div className={styles.stepLabel} style={{ marginTop: 18 }}>{`${t('충전 목표금액')} (${t('최소 입금금액')}: ${limits ? `${money(limits.minimum_deposit_amount)} USDT` : '-'})`}</div>
            <input
              className={styles.stepInputWide}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={limits ? `${t('충전 목표금액')}: ${money(limits.minimum_deposit_amount)} USDT ${t('이상')} (${t('0보다 큰 금액')})` : t('결제 금액 정책을 확인 중입니다.')}
            />

            <button className={styles.btnPink} type="button" onClick={onIssueDepositAddress} disabled={Boolean(busyAction) || Boolean(waitingRequestId) || activeDeposits.length > 0}>
              {busyAction === 'deposit-address' ? t('발급 중...') : t('입금주소 발급')}
            </button>

            {waitingRequestId ? (
              <div className={styles.messageBox} role="status">
                {t('입금 지갑 대기 중')} {waitingPosition > 0 ? `#${waitingPosition}` : ''}
                {' · '}{t('지갑이 배정되면 이 화면에서 자동으로 확인합니다.')}
              </div>
            ) : null}
            <div className={styles.stepLabel} style={{ marginTop: 18 }}>{t('입금 지갑 주소')}</div>
            <div className={styles.networkRow}>
              <span className={styles.netBadge}>T</span>
              <span className={styles.networkText}>{paymentNetwork.label}</span>
            </div>

            <div className={styles.addrBox}>
              <input className={styles.addrInput} value={depositWallet || ''} readOnly placeholder={t('입금주소 발급 후 표시됩니다.')} />
              <button className={styles.copyBtn} type="button" title={t('복사')} onClick={async () => {
                if (!depositWallet) return;
                const ok = await copyText(depositWallet);
                if (!ok) alert(t('복사 실패'));
              }}>⧉</button>
            </div>

            <div className={styles.warnRed} style={{ marginTop: 10 }}>
              {t('반드시 표시된 {networkLabel} 주소로 송금하세요. 송금 전에 충전 목표금액과 배정 주소를 확인하세요.', { networkLabel: paymentNetwork.label })}
            </div>

            <button className={`${styles.btnYellow} ${!canSubmitDeposit ? styles.btnDisabled : ''}`} type="button" onClick={onSubmitDeposit} disabled={!canSubmitDeposit}>
              {busyAction === 'deposit-request' ? t('등록 중...') : t('송금 완료 후 충전 확인 요청')}
            </button>

            <section className={styles.activeDepositSection} aria-label={t('진행 중 충전 신청')}>
              <div className={styles.activeDepositHeader}>
                <span>{t('진행 중 충전 신청')}</span>
                <span className={styles.activeDepositCount}>{activeDeposits.length}</span>
              </div>
              {activeDeposits.length === 0 ? (
                <div className={styles.activeDepositEmpty}>{t('진행 중인 충전 신청이 없습니다. 신청하면 이곳에 기록이 표시됩니다.')}</div>
              ) : activeDeposits.map((item) => (
                <div className={styles.activeDepositItem} key={item.request_id}>
                  <div className={styles.activeDepositDetails}>
                    <div className={styles.activeDepositTopLine}>
                      <span className={styles.activeDepositStatus}>{item.wallet_queue_waiting ? t('입금지갑 배정 대기') : t('진행 중')}</span>
                      <strong>{item.amount != null ? `${money(item.amount)} USDT` : t('충전 신청')}</strong>
                    </div>
                    <div className={styles.activeDepositMeta} title={String(item.request_id)}>
                      {t('요청ID')}: {shortAddress(item.request_id)}
                      {item.wallet_queue_waiting && Number(item.wallet_queue_position || 0) > 0 ? ` · #${item.wallet_queue_position}` : ''}
                    </div>
                    {item.deposit_session_expires_at ? (
                      <div className={styles.activeDepositMeta}>
                        {t('만료 시각')}: {new Date(item.deposit_session_expires_at).toLocaleString()}
                      </div>
                    ) : null}
                    {hasReceivedDeposit(item) ? (
                      <div className={styles.activeDepositCaution}>{t('입금이 감지되어 취소할 수 없습니다. 재확인 또는 운영자 검토가 필요합니다.')}</div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={styles.btnCancelDeposit}
                    onClick={() => onCancelDeposit(item)}
                    disabled={Boolean(busyAction) || hasReceivedDeposit(item)}
                  >
                    {busyAction === `cancel-deposit:${item.request_id}` ? t('취소 중...') : t('신청 취소')}
                  </button>
                </div>
              ))}
              <div className={styles.activeDepositFootnote}>
                {t('취소는 온체인 송금을 되돌리지 않습니다. 이미 송금한 경우에는 취소하지 마세요.')}
              </div>
            </section>
          </div>
        ) : (
          <div className={styles.stepBlock}>
            <div className={styles.stepTitle}>{t('Admin Exchange')}</div>
            <div className={styles.step2HeaderRow}>
              <div className={styles.stepLabelInline}>{t('받는 지갑')}</div>
              <div className={styles.networkRowInline}>
                <span className={styles.netBadge}>T</span>
                <span className={styles.networkText}>{paymentNetwork.label}</span>
              </div>
            </div>
            <input
              className={styles.stepInputWide}
              value={withdrawAddress}
              onChange={(e) => setWithdrawAddress(e.target.value)}
              placeholder={t('환전 받을 USDT TRC20 메인넷 주소')}
            />

            <div className={styles.stepLabel} style={{ marginTop: 18 }}>{t('금액')}</div>
            <input
              className={styles.stepInputWide}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('환전 금액')}
            />

            <div className={styles.warnRed} style={{ marginTop: 10 }}>
              {t('환전은 DB 자산과 출금가능 검증금액을 확인하고 진행됩니다. 운영수익은 개인 출금가능금액에 포함하지 않습니다.')}
            </div>

            <button className={`${styles.btnYellow} ${!canSubmitWithdraw ? styles.btnDisabled : ''}`} type="button" onClick={onSubmitWithdraw} disabled={!canSubmitWithdraw}>
              {busyAction === 'withdraw-request' ? t('등록 중...') : t('환전 요청')}
            </button>
          </div>
        )}

        <div className={styles.historyHeader}>
          <div className={styles.stepTitle}>{t('최근 결제 요청')}</div>
          <button className={styles.btnOutlineSmall} type="button" onClick={() => runAction('refresh', loadData)} disabled={Boolean(busyAction)}>
            {t('새로고침')}
          </button>
        </div>

        <div className={styles.historyTableWrap}>
          <table className={styles.historyTable}>
            <thead>
              <tr>
                <th>{t('구분')}</th>
                <th>{t('상태')}</th>
                <th>{t('금액')}</th>
                <th>{t('주소')}</th>
                <th>{t('요청ID')}</th>
                <th>{t('관리')}</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan="6" className={styles.emptyCell}>{loading ? t('불러오는 중...') : t('결제 요청 내역이 없습니다.')}</td></tr>
              ) : history.map((item) => (
                <tr key={item.request_id}>
                  <td>{requestTypeLabel(item.request_type)}</td>
                  <td>{statusLabel(item.status)}{item.deposit_session_expires_at && String(item.status).toUpperCase() === 'IN_PROGRESS' ? <small title={t('충전 세션 만료 시각')}>{new Date(item.deposit_session_expires_at).toLocaleString()}</small> : null}</td>
                  <td>{money(item.amount)}</td>
                  <td title={item.address || item.deposit_wallet_address || item.wallet_address || ''}>{shortAddress(item.address || item.deposit_wallet_address || item.wallet_address)}</td>
                  <td title={item.request_id}>{shortAddress(item.request_id)}</td>
                  <td>
                    <div className={styles.rowButtons}>
                      {String(item.request_type || '').toLowerCase() === 'deposit' ? (
                        <>
                          <button type="button" className={styles.btnMini} onClick={() => onRecheck(item.request_id)} disabled={Boolean(busyAction)}>{t('재확인')}</button>
                          {String(item.status || '').toUpperCase() === 'IN_PROGRESS'
                            && item.deposit_session_expires_at
                            && Date.parse(item.deposit_session_expires_at) > Date.now()
                            && (!item.deposit_session_max_expires_at || Date.parse(item.deposit_session_max_expires_at) > Date.parse(item.deposit_session_expires_at)) ? (
                            <button type="button" className={styles.btnMini} onClick={() => onExtend(item)} disabled={Boolean(busyAction)}>{t('30분 연장')}</button>
                          ) : null}
                        </>
                      ) : null}
                      {isActiveDeposit(item) ? (
                        <button type="button" className={styles.btnMiniDanger} onClick={() => onCancelDeposit(item)} disabled={Boolean(busyAction) || hasReceivedDeposit(item)}>{t('취소')}</button>
                      ) : String(item.request_type || '').toLowerCase() === 'withdraw'
                        && String(item.status || '').toUpperCase() === 'IN_PROGRESS' ? (
                        <button type="button" className={styles.btnMini} onClick={() => onCancel(item.request_id)} disabled={Boolean(busyAction)}>{t('취소')}</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
